import { newPasswordSchema, PASSWORD_MIN_LENGTH } from '@menage/shared';
import { Button, Card, Dialog, DialogContent, Field, Input } from '@menage/ui';
import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth/AuthProvider';
import { usePasswordErrorMessage } from './passwordErrors';

const EMPTY = { current: '', next: '', confirm: '' };

/** The form itself: works for local and Supabase sessions (the auth client decides). */
export function ChangePasswordForm({ onDone, onCancel }: { onDone?: () => void; onCancel?: () => void }) {
  const { t } = useTranslation('auth');
  const { client } = useAuth();
  const errorMessage = usePasswordErrorMessage();
  const [form, setForm] = useState(EMPTY);
  const [fieldError, setFieldError] = useState<{ current?: string; next?: string; confirm?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <form
      className="space-y-4"
      noValidate
      onSubmit={async (e) => {
        e.preventDefault();
        const errs: typeof fieldError = {};
        if (!form.current) errs.current = t('common:errors.VALIDATION_ERROR');
        if (!newPasswordSchema.safeParse(form.next).success) errs.next = t('passwordPolicy', { min: PASSWORD_MIN_LENGTH });
        else if (form.next !== form.confirm) errs.confirm = t('mismatch');
        setFieldError(errs);
        if (errs.current || errs.next || errs.confirm) return;
        setError(null);
        setBusy(true);
        try {
          await client.changePassword(form.current, form.next);
          setForm(EMPTY);
          toast.success(t('change.success'));
          onDone?.();
        } catch (err) {
          setError(errorMessage(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      <Field label={t('change.current')} htmlFor="pw-current" required error={fieldError.current}>
        <Input id="pw-current" type="password" autoComplete="current-password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} aria-invalid={!!fieldError.current} />
      </Field>
      <Field label={t('change.next')} htmlFor="pw-next" required hint={fieldError.next ? undefined : t('passwordPolicy', { min: PASSWORD_MIN_LENGTH })} error={fieldError.next}>
        <Input id="pw-next" type="password" autoComplete="new-password" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} aria-invalid={!!fieldError.next} />
      </Field>
      <Field label={t('change.confirm')} htmlFor="pw-confirm" required error={fieldError.confirm}>
        <Input id="pw-confirm" type="password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} aria-invalid={!!fieldError.confirm} />
      </Field>
      {error ? (
        <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Link to="/mot-de-passe-oublie" className="text-sm font-medium text-primary hover:underline">
          {t('change.forgot')}
        </Link>
        <div className="flex gap-2">
          {onCancel ? (
            <Button type="button" variant="outline" onClick={onCancel}>
              {t('change.cancel')}
            </Button>
          ) : null}
          <Button type="submit" loading={busy}>
            {t('change.submit')}
          </Button>
        </div>
      </div>
    </form>
  );
}

/** Account / profile section: collapsed until the user asks to change the password. */
export function ChangePasswordCard() {
  const { t } = useTranslation('auth');
  const [open, setOpen] = useState(false);
  return (
    <Card className="p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <KeyRound className="size-5 text-muted-foreground" aria-hidden />
          <div>
            <p className="font-medium">{t('change.title')}</p>
            <p className="text-sm text-muted-foreground">{t('change.subtitle')}</p>
          </div>
        </div>
        {!open ? (
          <Button variant="outline" onClick={() => setOpen(true)}>
            {t('change.open')}
          </Button>
        ) : null}
      </div>
      {open ? (
        <div className="mt-4">
          <ChangePasswordForm onDone={() => setOpen(false)} onCancel={() => setOpen(false)} />
        </div>
      ) : null}
    </Card>
  );
}

/** Same form in a dialog (e.g. from a user menu where there is no profile page). */
export function ChangePasswordDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation('auth');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t('change.open')} description={t('change.subtitle')}>
        <ChangePasswordForm onDone={() => onOpenChange(false)} onCancel={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

/** For a menu (staff have no profile page): `open()` from a menu item, render `dialog` outside the menu. */
export function useChangePasswordDialog() {
  const { t } = useTranslation('auth');
  const [open, setOpen] = useState(false);
  return { label: t('change.open'), open: () => setOpen(true), dialog: <ChangePasswordDialog open={open} onOpenChange={setOpen} /> };
}
